import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IUserSessionData } from 'app/store/models/user.model';
import { ISelectItem } from 'app/store/models/select.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { Subject } from 'rxjs';
import { SessionService } from 'app/store/services/session.service';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'app/store/services/user.service';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { getUserSession } from 'app/store/actions/user.action';
import { openSnackbar } from '../../store/actions/page.action';
import { UtilitiesService } from '../../store/services/utilities.servive';

@Component({
  selector: 'app-update-personal-info',
  templateUrl: './update-personal-info.component.html',
  styleUrls: ['./update-personal-info.component.scss'],
})
export class UpdatePersonalInfoComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;

  sessionData: IUserSessionData;

  refreshTime: ISelectItem[];

  refreshTimeValue: number;

  cancelButtonProperties: Button = {
    text: 'Cancel',
    color: ButtonColor.RED,
    size: ButtonSize.NORMAL,
    route: ['/accounts', 'settings'],
    style: 'outline',
  };

  continueButtonProperties: Button = {
    text: 'Save',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    onClick: () => this.submitForm(),
    style: 'rounded',
  };

  currentCountry: string;
  states: ISelectItem[];
  countries: ISelectItem[];
  formReady: boolean;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private userService: UserService,
    private store: Store<IReducers>,
    private utilService: UtilitiesService,
  ) {
  }

  submitForm() {
    if (this.formGroup.valid) {
      this.userService
        .updateUserPersonalInfo(this.sessionData.id, {
          ...this.formGroup.value,
        })
        .subscribe(res => {
          this.store.dispatch(getUserSession());
          this.store.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  type: 'success',
                  message: 'Your personal info was updated',
                },
                duration: 5000,
                verticalPosition: 'bottom',
              },
            }),
          );
        });
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;
        this.userService.getBillingInfo(this.sessionData.id)
          .subscribe((response: IUserSessionData) => {
            this.currentCountry = response.country || 'US';
            this.refreshTimeValue = response.inbox_refresh_time;
            this.countries = Object.keys(this.utilService.getCountries()).map(
              country => {
                return {
                  label: this.utilService.getCountries()[country],
                  value: country,
                };
              },
            );
            this.refreshTime = [
              {
                label: 'Auto',
                value: this.sessionData.default_refresh_time,
              },
              {
                label: '20 seconds',
                value: 20,
              },
              {
                label: '1 minute',
                value: 60,
              },
              {
                label: 'Off',
                value: 0,
              },
            ];

            this.formGroup = new FormGroup({
              first_name: new FormControl(response.first_name, [
                Validators.required,
              ]),
              last_name: new FormControl(response.last_name, [
                Validators.required,
              ]),
              company: new FormControl(response.company, [
                Validators.required,
              ]),
              inbox_refresh_time: new FormControl(
                response.inbox_refresh_time,
                [Validators.required],
              ),
              phone_number: new FormControl(
                response.phone_number
                  ? response.phone_number
                  : '',
                [
                  Validators.required,
                  Validators.minLength(10),
                  Validators.maxLength(10),
                ],
              ),
              city: new FormControl(
                response.city
                  ? this.sessionData.city
                  : '',
                [Validators.required],
              ),
              state: new FormControl(
                response.state
                  ? response.state
                  : '',
                [Validators.required],
              ),
              zip: new FormControl(
                response.zip
                  ? response.zip
                  : '',
                [Validators.required],
              ),
              country: new FormControl(this.currentCountry, [
                Validators.required,
              ]),
            });
            this.onCountrySelect(this.currentCountry);
            this.formReady = true;
          });
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  onCountrySelect(value: string) {
    this.currentCountry = value;
    const states = this.utilService.getStates();

    if (states[value]) {
      this.states = Object.keys(states[value]).map(state => {
        return {
          label: states[value][state],
          value: state,
        };
      });
    }
  }
}
